'use client';

import type { QuoteBlock } from '@impact/types';
import { cn } from '@/lib/utils';
import { Quote } from 'lucide-react';
import { QuoteMagazinePullquote } from './quote-variants/MagazinePullquote';
import { QuoteCard } from './quote-variants/Card';

interface QuoteBlockRendererProps {
  block: QuoteBlock;
  editing?: boolean;
}

/**
 * Renders a quote/testimonial block
 */
export function QuoteBlockRenderer({ block, editing }: QuoteBlockRendererProps) {
  switch (block.data.variant ?? 'default') {
    case 'magazine-pullquote':
      return <QuoteMagazinePullquote block={block} editing={editing} />;
    case 'card':
      return <QuoteCard block={block} editing={editing} />;
    default:
      break;
  }

  const { text, author, role, imageUrl, style = 'simple' } = block.data;

  if (style === 'large') {
    return (
      <div className="my-16 py-12 bg-muted/30 rounded-2xl">
        <div className="max-w-3xl mx-auto text-center px-8">
          <Quote className="h-12 w-12 text-primary/30 mx-auto mb-6" />
          <blockquote
            className="text-2xl md:text-3xl font-medium text-foreground italic leading-relaxed"
            {...(editing ? { 'data-editable': 'data.text' } : {})}
          >
            &ldquo;{text}&rdquo;
          </blockquote>
          {(author || role) && (
            <div className="mt-8 flex items-center justify-center gap-4">
              {imageUrl && (
                <div className="relative h-14 w-14 rounded-full overflow-hidden">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={imageUrl}
                    alt={author || 'Quote author'}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </div>
              )}
              <div className="text-left">
                {author && (
                  <div
                    className="font-semibold text-foreground"
                    {...(editing ? { 'data-editable': 'data.author' } : {})}
                  >
                    {author}
                  </div>
                )}
                {role && (
                  <div
                    className="text-sm text-muted-foreground"
                    {...(editing ? { 'data-editable': 'data.role' } : {})}
                  >
                    {role}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (style === 'card') {
    return (
      <div className="my-12 p-8 bg-muted/30 rounded-xl border border-border/50">
        <Quote className="h-8 w-8 text-primary/40 mb-4" />
        <blockquote
          className="text-lg text-foreground italic"
          {...(editing ? { 'data-editable': 'data.text' } : {})}
        >
          &ldquo;{text}&rdquo;
        </blockquote>
        {(author || role) && (
          <div className="mt-6 flex items-center gap-4">
            {imageUrl && (
              <div className="relative h-12 w-12 rounded-full overflow-hidden">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={imageUrl}
                  alt={author || 'Quote author'}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
            )}
            <div>
              {author && (
                <div
                  className="font-semibold text-foreground"
                  {...(editing ? { 'data-editable': 'data.author' } : {})}
                >
                  {author}
                </div>
              )}
              {role && (
                <div
                  className="text-sm text-muted-foreground"
                  {...(editing ? { 'data-editable': 'data.role' } : {})}
                >
                  {role}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Simple style (default)
  return (
    <div className="my-12 pl-6 border-l-4 border-primary/30">
      <blockquote
        className="text-xl text-foreground italic"
        {...(editing ? { 'data-editable': 'data.text' } : {})}
      >
        &ldquo;{text}&rdquo;
      </blockquote>
      {(author || role) && (
        <div className={cn('mt-4 flex items-center gap-3', imageUrl && 'mt-6')}>
          {imageUrl && (
            <div className="relative h-10 w-10 rounded-full overflow-hidden">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={imageUrl}
                alt={author || 'Quote author'}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          )}
          <div>
            {author && (
              <span
                className="font-medium text-foreground"
                {...(editing ? { 'data-editable': 'data.author' } : {})}
              >
                {author}
              </span>
            )}
            {author && role && <span className="text-muted-foreground"> — </span>}
            {role && (
              <span
                className="text-muted-foreground"
                {...(editing ? { 'data-editable': 'data.role' } : {})}
              >
                {role}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
